import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AfterViewInit, Component, ComponentRef, ElementRef, HostListener, inject, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { gungComparatorHelper, GungDownloadUrl, GungImageType, GungImageUrlService } from 'gung-common';
import { catchError, filter, first, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';
import { BaseViewConfigService } from '../../../services/base-view-config/base-view-config.service';
import { GungFlowService } from '../../../services/gung-flow/gung-flow.service';
import { GungNavbarService, GungShopMenu } from '../../../services/gung-navbar/gung-navbar.service';
import { MetadataService } from '../../../services/metadata/metadata.service';
import { NavbarCartModel, NavbarCartService } from '../../../services/navbar-cart/navbar-cart.service';
import { ACTIVE_BRANDING_GROUP_RESOLVER } from '../../../services/group-branding/active-branding-group-resolver';
import { GroupBrandingApplyService } from '../../../services/group-branding/group-branding-apply.service';
import { GungFlow } from '../../../state/flow/types';
import { S3Image } from '../../../models/s3';
import { GungNavbarCartComponent } from '../gung-navbar-cart/gung-navbar-cart.component';

@Component({
  selector: 'lib-gung-navbar',
  templateUrl: './gung-navbar.component.html',
  styleUrls: ['./gung-navbar.component.scss'],
  standalone: false
})
export class GungNavbarComponent implements OnInit, OnDestroy, AfterViewInit {
  protected gungNavbar = inject(GungNavbarService);
  protected authService = inject(AuthService);
  protected navbarCartService = inject(NavbarCartService);
  protected gungFlowService = inject(GungFlowService);
  protected baseViewConfigService = inject(BaseViewConfigService);
  protected metadataService = inject(MetadataService);
  protected breakpointObserver = inject(BreakpointObserver);
  protected router = inject(Router);
  protected gungImageUrlService = inject(GungImageUrlService);
  protected groupBrandingApplyService = inject(GroupBrandingApplyService);
  protected activeGroupResolver = inject(ACTIVE_BRANDING_GROUP_RESOLVER);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const clickedInsideMenu = (event.target as HTMLElement).closest('.main-menu, .btn-open-menu');
    if (this.activeMainMenu && !clickedInsideMenu) {
      this.activeMainMenu = false;
    }
  }

  @ViewChild('iconCheckoutElement') iconCheckoutElement: ElementRef | undefined;

  @ViewChild('navbarCart', { read: ViewContainerRef }) viewChild;

  dynamicComponentRef: ComponentRef<GungNavbarCartComponent>;

  unsubscribe: Subject<void> = new Subject<void>();

  public logoUrl; /* = this.gungNavbar.getLogoUrl(); */
  isSales = false;
  activeMainMenu = false;
  isClosingShowMenu = false;
  shopMenu: GungShopMenu[];
  mobileShopMenu: GungShopMenu[];
  customerInfo: { id: string; description: string };
  isAnonymous = true;
  authenticated: boolean;
  isGlobalSearchOpen = false;
  cartContents: NavbarCartModel;
  showflowMenu = false;
  flowsMenu: GungShopMenu[];
  selectedFlow: GungFlow;
  navbarHeight = this.gungNavbar.navbarHeight;
  mobileDeviceResolution = 768;
  mobileDevice = false;
  forceToNotShowFlowsOnTop = this.gungNavbar.forceToNotShowFlowsOnTop;
  hideCartDropdown = false;
  currentUrl = this.router.url;
  hideMyCustomers = this.gungNavbar.hideMyCustomers;
  showMyAccount = this.gungNavbar.showMyAccount;
  showLanguageSelection = this.gungNavbar.showLanguageSelection;
  hideFavorites = this.gungNavbar.hideFavorites;
  hideSelectedCustomer = this.gungNavbar.hideSelectedCustomer;
  hideCart = this.gungNavbar.hideCart;
  navbarBanner = undefined;

  ngOnInit(): void {
    this.activeGroupResolver.getActiveGroup().pipe(
      switchMap(group => this.groupBrandingApplyService.resolveBanner(group)),
      takeUntil(this.unsubscribe)
    ).subscribe(banner => {
      this.navbarBanner = banner;
      if (this.navbarBanner?.enabled) {
        this.navbarHeight = this.gungNavbar.navbarHeight + (Number(this.navbarBanner?.bannerHeight) || 0);
        this.breakpointObserver.observe(['(max-width:' + this.mobileDeviceResolution + 'px)']).pipe(first()).subscribe((state: BreakpointState) => {
          this.mobileDevice = state.matches;
          if (this.showflowMenu) {
            if (this.mobileDevice) {
              this.navbarHeight = this.gungNavbar.navbarHeight + 25;
            } else {
              this.navbarHeight = this.gungNavbar.navbarHeight;
            }
            if (this.navbarBanner?.enabled) {
              this.navbarHeight += Number(this.navbarBanner?.bannerHeight) || 0;
            }
          }
        });
      }
    });

    this.authService
      .getIsAuthenticated()
      .pipe(
        filter(authenticated => !!authenticated),
        takeUntil(this.unsubscribe)
      )
      .subscribe(auth => (this.authenticated = auth));

    this.activeGroupResolver.getActiveGroup().pipe(
      switchMap(group => group
        ? this.groupBrandingApplyService.resolveLogo(group).pipe(
            map(logo => this.brandingLogoUrl(logo))
          )
        : this.gungNavbar.getLogoUrlObservable()),
      takeUntil(this.unsubscribe)
    ).subscribe(logoUrl => {
      this.logoUrl = logoUrl;
    });

    this.authService
      .getRoles()
      .pipe(
        filter(roles => !!roles),
        first()
      )
      .subscribe(roles => {
        if (roles.indexOf('SALES') > -1 || roles.indexOf('ADMIN') > -1) {
          this.isSales = true;
        }
        if (roles.indexOf('ANONYMOUS') < 0) {
          this.isAnonymous = false;
        }
        this.gungNavbar
          .getShopMenu()
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(shopMenu => {
            this.shopMenu = shopMenu;

            this.mobileShopMenu = [{
              heading: 'MENU',
              links: this.shopMenu
            }];

            if (roles.indexOf('SUPPLIER') > -1 || roles.indexOf('BUYER') > -1) {
              this.shopMenu.unshift(...this.getSupplierBasedNavigation());
            }
          });
      });

    this.gungNavbar
      .getCustomerInfo()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(customerInfo => (this.customerInfo = customerInfo));

    this.navbarCartService.getContents().subscribe(contents => {
      this.cartContents = contents;
      this.updateNavCartComponent();
    });

    this.gungFlowService
      .getSelectedFlow()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(flow => (this.selectedFlow = flow));

    // The navbar service loads these flags from the company config over HTTP, so the field
    // initialisers above only ever see the defaults. Re-read them once the config has landed,
    // otherwise whether e.g. the flow menu is hidden comes down to who wins the race.
    this.gungNavbar
      .applyCompanyNavbarConfig()
      .pipe(
        // A failed company call must not cost us the flow menu entirely - fall back to the
        // defaults the fields were initialised with, which is what happened before.
        catchError(() => of(undefined)),
        first(),
        takeUntil(this.unsubscribe)
      )
      .subscribe(() => {
        this.applyNavbarConfig();

        if (this.gungNavbar.isAssortmentBased) {
          this.mountFlowMenu();
        }
      });

    this.breakpointObserver
      .observe(['(max-width:' + this.mobileDeviceResolution + 'px)'])
      .subscribe((state: BreakpointState) => {

        this.mobileDevice = state.matches;
        if (this.showflowMenu) {
          if (this.mobileDevice) {
            this.navbarHeight = this.gungNavbar.navbarHeight + 25;
          } else {
            this.navbarHeight = this.gungNavbar.navbarHeight;
          }
          if (this.navbarBanner?.enabled) {
            this.navbarHeight += Number(this.navbarBanner?.bannerHeight) || 0;
          }
        }

        this.checkRouter(this.router.url);

        this.router.events.subscribe((event) => {
          if (event instanceof NavigationEnd) {
            this.checkRouter(event.urlAfterRedirects);
          }
        });
      });

    this.authService.getCurrentUser().pipe(
      filter(user => !!user),
      first()
    ).subscribe(currentUser => {
      if (currentUser?.extra?.blockUserOrdering) {
        this.hideCart = true;
      }
    });
  }

  private brandingLogoUrl(logo: S3Image | null): string | null {
    const uri = logo?.s3Uri;
    if (!uri || uri.startsWith('https://')) {
      return null;
    }
    return uri.endsWith('.gif') ? GungDownloadUrl(uri) : this.gungImageUrlService.getUrl(uri);
  }

  protected checkRouter(url: string): void {
    this.currentUrl = url;
    if (url.includes('checkout')) {
      if (this.mobileDevice) {
        this.hideCartDropdown = true;
      } else {
        this.hideCartDropdown = false;
      }
    } else {
      this.hideCartDropdown = false;
    }
  }

  ngAfterViewInit(): void {
    this.renderLayout(this.baseViewConfigService.getNavbarCartBox());
  }

  protected applyNavbarConfig(): void {
    this.forceToNotShowFlowsOnTop = this.gungNavbar.forceToNotShowFlowsOnTop;
    this.hideMyCustomers = this.gungNavbar.hideMyCustomers;
    this.showMyAccount = this.gungNavbar.showMyAccount;
    this.showLanguageSelection = this.gungNavbar.showLanguageSelection;
    this.hideFavorites = this.gungNavbar.hideFavorites;
    this.hideSelectedCustomer = this.gungNavbar.hideSelectedCustomer;
    // Never un-hide: blockUserOrdering may already have hidden the cart for this user.
    this.hideCart = this.hideCart || this.gungNavbar.hideCart;
  }

  mountFlowMenu() {
    this.gungNavbar
      .getFlowMenu()
      .pipe(first())
      .subscribe(flows => {
        this.flowsMenu = flows;
        this.showflowMenu = flows.length > 1;
      });
  }

  toggleMainMenu() {
    this.activeMainMenu = !this.activeMainMenu;
  }

  menuClickEvent(event) {
    this.activeMainMenu = !this.activeMainMenu;
  }

  onSMenuEnter(event: MouseEvent): void {
    const sMenuEl = event.currentTarget as HTMLElement;
    const expandMenu = sMenuEl.querySelector('.expand-menu') as HTMLElement;
    if (!expandMenu) return;
    const secondMenu = expandMenu.querySelector('.second-menu') as HTMLElement;
    if (!secondMenu) return;

    // Reset to natural CSS width before measuring
    secondMenu.style.width = '';
    void expandMenu.offsetWidth; // force reflow

    const rect = expandMenu.getBoundingClientRect();
    const overflow = rect.right - (window.innerWidth - 10);
    if (overflow > 0) {
      secondMenu.style.width = `${secondMenu.offsetWidth - overflow}px`;
    }
  }

  closeExpandMenu(event) {
    this.isClosingShowMenu = event;
    setTimeout(() => {
      this.isClosingShowMenu = false;
    }, 100);
  }

  renderLayout(layoutComponent: Type<GungNavbarCartComponent>): void {
    this.dynamicComponentRef = this.viewChild?.createComponent(layoutComponent);
    this.dynamicComponentRef?.instance?.cartContentsSubject?.next(this.cartContents);
    this.dynamicComponentRef?.instance.close.subscribe(() => {
      this.closeDropdown();
    });
  }

  closeDropdown(): void {
    if (this.iconCheckoutElement) {
      this.hideCartDropdown = true;
      setTimeout(() => {
        this.hideCartDropdown = false;
      }, 100);
    }
  }

  updateNavCartComponent(): void {
    if (this.dynamicComponentRef) {
      this.dynamicComponentRef.instance.cartContentsSubject.next(this.cartContents);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  getSupplierBasedNavigation(): GungShopMenu[] {
    let postatus = this.metadataService.getMetadataTableList('POSTATUS');
    const postatusLinks: GungShopMenu[] = [];
    if (postatus?.length > 0) {
      postatus = Array(...postatus).sort((a, b) => gungComparatorHelper(a.id, b.id, 1)) as any;
      postatusLinks.push(
        ...postatus.map(p => ({
          heading: p.name,
          link: 'suppliers-purchase-order/' + p.id
        }))
      );
    } else {
      console.error(`NOTE: Supplier portal need metadata POSTATUS to work correctly`);
    }
    return postatusLinks;
  }

  getBannerImageUrl(): string {
    const image = this.navbarBanner?.navbarBannerImage;
    if (!image?.s3Uri || !image?.s3ETag) {
      return '';
    }
    const url = this.gungImageUrlService.getUrl(image.s3Uri, { etag: image.s3ETag, type: GungImageType.background, size: undefined, fill: undefined, background_color: undefined });
    return encodeURI(url)
  }
}
